import { useNavigationParams } from '../navigation/history'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthSession } from '../../lib/authSession'
import { useRead } from '../navigation/useRead'
import {
  recordHref,
  recordQuery,
  workbenchHref,
  subchainOverviewHref,
  isBookDiscoveryCard,
  type RecordContext,
  type ViewScope,
} from '../navigation/context'
import type { DocumentRef } from '../../lib/documentReader'
import SourceDocumentList from '../documents/SourceDocumentList'
import { ErrorState, Skeleton } from '../state'
import BookWorkContent from '../rkdecks/BookWorkContent'
import RkDeck from '../rkdecks/RkDeck'
import { sameManualBookContext, type ManualBookSelection } from '../rkdecks/manualBookRouting'
import { createBookPendingGroup } from '../rkdecks/bookWorkLifecycle'
import { bookCardAnchor, bookContinuation, type BookCardAnchor } from '../rkdecks/bookContinuation'
import { fetchBookWork, type BookWorkResponse } from '../rkdecks/bookWorkApi'
import type { BookReviewScope } from '../rkdecks/bookReviewQuery'
import WorkbenchHeader from './WorkbenchHeader'
import ContextTaskPane from './ContextTaskPane'
import EvidenceGapPane from '../evidence/EvidenceGapPane'
import ContextRecovery from './ContextRecovery'
import DocumentSourceQuestions from './DocumentSourceQuestions'
import { bookGroupForCard } from './documentSourceQuestions'
import { recoveryOwnsSelection } from './recovery'
import RemovalRequestPane from '../recordRemovals/RemovalRequestPane'
import {
  cardContextLabel,
  contextWorkView,
  scopeLabel,
  tasksForQueue,
  requirementTargetLabel,
  type ContextCardView,
  type ContextQueue,
  type ContextTask,
} from './contextWork'
import { SURFACE_SLUGS, surfaceFor } from './placement'
import '../rkdecks/rkdecks.css'
import './context-workbench.css'

const QUEUES: readonly ContextQueue[] = ['book', 'evidence', 'chase']
const QUEUE_SLUGS: Record<ContextQueue, string> = {
  book: 'record-keeping',
  evidence: 'ownership-verification',
  chase: 'chase-request',
}

function isQueue(value: string | null): value is ContextQueue {
  return value !== null && QUEUES.includes(value as ContextQueue)
}

function taskLabel(task: ContextTask): string {
  const labels: Record<string, string> = {
    proposal_review: 'Review booking',
    evidence_approval: 'Review evidence',
    verification_signoff: 'Review sign-off',
    verification_remediation: 'Review sign-off',
    investigation_action: 'Review document request',
    request_overdue: 'Review overdue request',
    matured_unconfirmed: 'Review closure document',
    position_trade_missing: 'Review missing movement',
    position_balance_stale: 'Review stale balance',
    instrument_trade_missing: 'Review missing movement',
  }
  return labels[task.task_type] ?? 'Open task'
}

function taskHref(
  task: ContextTask,
  queue: ContextQueue,
  scope: ViewScope,
  item: ContextCardView
): string {
  const taskQueue =
    QUEUES.find((candidate) => tasksForQueue(item, candidate).some((row) => row.id === task.id)) ??
    queue
  const surface = surfaceFor(task.task_type)
  const slug = surface ? SURFACE_SLUGS[surface] : QUEUE_SLUGS[taskQueue]
  const params = new URLSearchParams({
    scope: scope.kind,
    queue: taskQueue,
    card: item.card.id,
    task: task.id,
  })
  if (scope.client_id) params.set('client', scope.client_id)
  if (scope.fund_id) params.set('fund', scope.fund_id)
  if (scope.subchain_id) params.set('subchain', scope.subchain_id)
  if (scope.below_entity_id) params.set('below', scope.below_entity_id)
  return `/v2/workbench/${slug}/task/${encodeURIComponent(task.id)}?${params.toString()}`
}

interface BookReviewGroup {
  groupId: string
  proposalIds: readonly string[]
  heldItemIds?: readonly string[]
  reviewScope?: BookReviewScope
  scopeUnavailable?: boolean
  manualSelection?: ManualBookSelection
}

interface BookReviewRead {
  groupId: string
  status: 'loading' | 'ready' | 'error'
  work?: BookWorkResponse
  manualSelection?: ManualBookSelection
  error?: unknown
}

function proposalGroups(item: ContextCardView | undefined): readonly BookReviewGroup[] {
  if (!item) return []
  if (isBookDiscoveryCard(item.card)) {
    const card = item.card
    const groupId = bookGroupForCard(card)
    if (!groupId) return []
    return [
      {
        groupId,
        proposalIds: card.proposal_ids,
        heldItemIds: card.held_item_ids,
        reviewScope: card.context_fund_id
          ? { fundId: card.context_fund_id }
          : card.source_client_id
            ? { clientId: card.source_client_id }
            : undefined,
        scopeUnavailable: !card.context_fund_id && !card.source_client_id,
      },
    ]
  }
  const groups = new Map<string, string[]>()
  const pendingBookIds = new Set(item.card.book_proposal_ids)
  for (const proposal of item.proposals) {
    if (!proposal.group_id || proposal.status !== 'pending' || !pendingBookIds.has(proposal.id))
      continue
    groups.set(proposal.group_id, [...(groups.get(proposal.group_id) ?? []), proposal.id])
  }
  return [...groups.entries()].map(([groupId, proposalIds]) => {
    const proposals = item.proposals.filter((proposal) => proposalIds.includes(proposal.id))
    const marker = proposals.find((proposal) => proposal.manual_book_context)?.manual_book_context
    if (!marker) return { groupId, proposalIds }
    const valid =
      marker.group_id === groupId &&
      proposals.every((proposal) => sameManualBookContext(marker, proposal.manual_book_context))
    return {
      groupId,
      proposalIds,
      scopeUnavailable: !valid,
      manualSelection: valid ? { context: marker, proposalIds } : undefined,
    }
  })
}

function useBookReviews(
  item: ContextCardView | undefined,
  queue: ContextQueue,
  scope: ViewScope
): { reads: readonly BookReviewRead[]; refresh: () => void } {
  const session = useAuthSession()
  const groups = useMemo(() => (queue === 'book' ? proposalGroups(item) : []), [item, queue])
  const [retry, setRetry] = useState(0)
  const readKey = JSON.stringify([groups, item?.card.revision, scope, session, retry])
  const [result, setResult] = useState<{ key: string; reads: readonly BookReviewRead[] } | null>(
    null
  )
  useEffect(() => {
    let cancelled = false
    if (!session || !groups.length) {
      setResult({ key: readKey, reads: [] })
      return () => {
        cancelled = true
      }
    }
    setResult({
      key: readKey,
      reads: groups.map((group) => ({ groupId: group.groupId, status: 'loading' as const })),
    })
    const reviewScope: BookReviewScope = {
      fundId: scope.fund_id ?? null,
      clientId: scope.fund_id ? null : (scope.client_id ?? null),
    }
    Promise.all(
      groups.map(async (group): Promise<BookReviewRead> => {
        try {
          if (group.scopeUnavailable)
            throw new Error('This Book selection has no available review context.')
          if (group.manualSelection) {
            if (scope.fund_id && scope.fund_id !== group.manualSelection.context.fund_id)
              throw new Error('This manual Book selection belongs to a different Fund.')
            return {
              groupId: group.groupId,
              status: 'ready',
              manualSelection: group.manualSelection,
            }
          }
          const response = await fetchBookWork(
            group.groupId,
            group.proposalIds,
            group.reviewScope ?? reviewScope,
            false,
            group.heldItemIds ?? []
          )
          return {
            groupId: group.groupId,
            status: 'ready',
            work: response,
          }
        } catch (error) {
          return { groupId: group.groupId, status: 'error', error }
        }
      })
    ).then((result) => {
      if (!cancelled) setResult({ key: readKey, reads: result })
    })
    return () => {
      cancelled = true
    }
  }, [
    groups,
    scope.below_entity_id,
    scope.client_id,
    scope.fund_id,
    scope.kind,
    scope.subchain_id,
    session,
    retry,
    readKey,
  ])
  return {
    reads:
      result?.key === readKey
        ? result.reads
        : groups.map((group): BookReviewRead => ({ groupId: group.groupId, status: 'loading' })),
    refresh: () => setRetry((current) => current + 1),
  }
}

function uniqueDocuments(documents: readonly DocumentRef[]): readonly DocumentRef[] {
  const seen = new Set<string>()
  const result: DocumentRef[] = []
  for (const document of documents) {
    if (seen.has(document.document_id)) continue
    seen.add(document.document_id)
    result.push(document)
  }
  return result
}

function contextDoorHref(
  href: string,
  scope: ViewScope,
  queue: ContextQueue,
  cardId: string
): string {
  const [path, query = ''] = href.split('?')
  const params = new URLSearchParams(query)
  params.set('queue', queue)
  params.set('card', cardId)
  return `${path}?${params.toString()}`
}

function subchainDoor(
  scope: ViewScope,
  item: ContextCardView,
  _queue: ContextQueue
): string | null {
  if (isBookDiscoveryCard(item.card)) return null
  try {
    return subchainOverviewHref(item.card, scope)
  } catch {
    return null
  }
}

function CardSummary({
  item,
  selected,
  onSelect,
}: {
  item: ContextCardView
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      className={`context-workbench__card-summary${selected ? ' is-selected' : ''}`}
      aria-pressed={selected}
      data-testid={`context-card-${item.card.id}`}
      onClick={onSelect}
    >
      <span className="context-workbench__card-title">{item.card.label}</span>
      <span className="context-workbench__card-context">{item.queues.join(' · ')}</span>
    </button>
  )
}

function BookReviewPanels({
  reads,
  onRefresh,
  onLocalRefresh,
  onApplied,
  onPendingChange,
}: {
  reads: readonly BookReviewRead[]
  onRefresh: () => void
  onLocalRefresh: () => void
  onApplied: () => void
  onPendingChange: (pending: boolean) => void
}) {
  const localRefreshCallback = useRef(onLocalRefresh)
  localRefreshCallback.current = onLocalRefresh
  const refreshCallback = useRef(onRefresh)
  refreshCallback.current = onRefresh
  const pendingCallback = useRef(onPendingChange)
  pendingCallback.current = onPendingChange
  const [pendingGroup] = useState(() =>
    createBookPendingGroup(
      (pending) => pendingCallback.current(pending),
      () => refreshCallback.current(),
      () => localRefreshCallback.current()
    )
  )
  const owners = useRef(new Map<string, ReturnType<typeof pendingGroup.attach>>())
  useEffect(() => {
    pendingGroup.activate()
    const registered = owners.current
    return () => {
      pendingGroup.dispose()
      registered.clear()
    }
  }, [pendingGroup])
  const reportPending = (groupId: string, pending: boolean) => {
    let owner = owners.current.get(groupId)
    if (!owner) {
      if (!pending) return
      owner = pendingGroup.attach()
      owners.current.set(groupId, owner)
    }
    owner.set(pending)
  }
  if (!reads.length) return null
  return (
    <div className="context-workbench__book-reviews" data-testid="context-book-reviews">
      {reads.map((read) => (
        <section key={read.groupId} data-group-id={read.groupId}>
          {read.status === 'loading' ? <p role="status">Reading this Book review…</p> : null}
          {read.status === 'error' ? (
            <p className="context-workbench__review-unavailable">
              This Book review is unavailable for the current context.
            </p>
          ) : null}
          {read.status === 'ready' && read.manualSelection ? (
            <div className="rkdecks" style={{ height: 'auto', padding: 0, overflow: 'visible' }}>
              <RkDeck
                deckId={`group:${read.groupId}`}
                manualSelection={read.manualSelection}
                onDecision={onApplied}
                onBookPendingChange={(pending) => reportPending(read.groupId, pending)}
              />
            </div>
          ) : null}
          {read.status === 'ready' && read.work ? (
            <BookWorkContent
              groupId={read.groupId}
              work={read.work}
              onRefresh={pendingGroup.requestRefresh}
              onLocalRefresh={pendingGroup.requestLocalRefresh}
              onApplied={onApplied}
              onPendingChange={(pending) => reportPending(read.groupId, pending)}
            />
          ) : null}
        </section>
      ))}
    </div>
  )
}

function CardDetail({
  item,
  work,
  queue,
  scope,
  bookReviews,
  onRefresh,
  onLocalRefresh,
  onApplied,
  onBookPendingChange,
}: {
  item: ContextCardView
  work: RecordContext['work']
  queue: ContextQueue
  scope: ViewScope
  bookReviews: readonly BookReviewRead[]
  onRefresh: () => void
  onLocalRefresh: () => void
  onApplied: () => void
  onBookPendingChange: (pending: boolean) => void
}) {
  const [params, setParams] = useNavigationParams()
  const queueTasks = tasksForQueue(item, queue)
  const taskId = params.get('task')
  const activeTask = taskId ? queueTasks.find((task) => task.id === taskId) : queueTasks[0]
  const gaps =
    queue === 'evidence'
      ? item.requirements.filter(
          (requirement) =>
            requirement.queue === 'evidence' &&
            requirement.status !== 'pending_signoff' &&
            !queueTasks.some(
              (task) =>
                task.task_type === 'evidence_approval' && task.requirement_id === requirement.id
            ) &&
            (!taskId || activeTask?.requirement_id === requirement.id)
        )
      : []
  const gapOwnsTask =
    activeTask?.task_type === 'investigation_action' &&
    gaps.some((requirement) => requirement.id === activeTask.requirement_id)
  const fundId =
    scope.fund_id ??
    (isBookDiscoveryCard(item.card)
      ? item.card.context_fund_id
      : item.card.fund_ids.length === 1
        ? item.card.fund_ids[0]
        : null)
  const reviewDocuments = bookReviews.flatMap((read) => read.work?.documents ?? [])
  const documents = uniqueDocuments([...item.documents, ...reviewDocuments])
  const recordDoor = isBookDiscoveryCard(item.card)
    ? recordHref(scope)
    : contextDoorHref(recordHref(scope), scope, queue, item.card.id)
  const subchain = subchainDoor(scope, item, queue)
  const resolved = () => {
    if (queue === 'book') {
      onRefresh()
      return
    }
    // Only task completion releases the selection; plain refreshes and gap
    // changes (including Wake) retain their context.
    const next = new URLSearchParams(params)
    next.delete('task')
    next.delete('card')
    next.delete('recovery')
    setParams(next, { replace: true })
    onApplied()
  }
  return (
    <article className="context-workbench__card" data-testid="context-card-detail">
      <header className={`context-workbench__card-header queue-${queue}`}>
        <div>
          <p className="context-workbench__eyebrow">{queue}</p>
          <h2>{cardContextLabel(work, item.card)}</h2>
          <p className="context-workbench__sentence">{item.card.label}</p>
        </div>
      </header>

      <section className="context-workbench__section" aria-labelledby="context-doors-heading">
        <h3 id="context-doors-heading">Context</h3>
        <div className="context-workbench__task-links">
          <Link to={recordDoor}>Record →</Link>
          {subchain ? <Link to={subchain}>Subchain overview →</Link> : null}
          {item.tasks
            .filter(
              (task) => !queueTasks.some((row) => row.id === task.id) && task.status === 'open'
            )
            .map((task) => (
              <Link key={task.id} to={taskHref(task, queue, scope, item)}>
                {taskLabel(task)} →
              </Link>
            ))}
        </div>
      </section>

      {queue === 'book' ? (
        <BookReviewPanels
          key={item.card.id}
          reads={bookReviews}
          onRefresh={onRefresh}
          onLocalRefresh={onLocalRefresh}
          onApplied={onApplied}
          onPendingChange={onBookPendingChange}
        />
      ) : null}
      {queue === 'book' &&
      isBookDiscoveryCard(item.card) &&
      item.card.source_question_ids?.length ? (
        <DocumentSourceQuestions
          taskIds={item.card.source_question_ids}
          documentIds={item.card.document_ids}
          scope={
            item.card.context_fund_id
              ? { kind: 'fund', fund_id: item.card.context_fund_id }
              : item.card.source_client_id
                ? { kind: 'client', client_id: item.card.source_client_id }
                : scope
          }
          revision={item.card.revision}
          onChanged={onRefresh}
        />
      ) : null}
      {queue === 'book'
        ? item.removalRequests.map((request) => (
            <RemovalRequestPane
              key={request.id}
              requestId={request.id}
              embedded
              targetLabel={request.label ?? cardContextLabel(work, item.card)}
              recordHref={recordDoor}
              overviewHref={subchain ?? undefined}
              onChanged={onRefresh}
            />
          ))
        : null}
      {taskId &&
      !activeTask &&
      !(isBookDiscoveryCard(item.card) && item.card.source_question_ids?.includes(taskId)) ? (
        <p role="status">The selected task is no longer open in this queue.</p>
      ) : null}
      {queueTasks.length > 1 ? (
        <nav className="context-workbench__task-tabs" aria-label="Open decisions in this subchain">
          {queueTasks.map((task) => (
            <button
              type="button"
              key={task.id}
              aria-pressed={task.id === activeTask?.id}
              onClick={() => {
                const next = new URLSearchParams(params)
                next.set('task', task.id)
                setParams(next)
              }}
            >
              {taskLabel(task)}
            </button>
          ))}
        </nav>
      ) : null}
      {gaps.map((requirement) => (
        <EvidenceGapPane
          key={requirement.id}
          requirement={requirement}
          targetLabel={requirementTargetLabel(work, requirement)}
          fundId={fundId}
          recordHref={recordHref(scope, {
            focus: `${requirement.target.kind}:${requirement.target.id}`,
          })}
          chaseHref={`${workbenchHref(scope, 'chase')}&card=${encodeURIComponent(item.card.id)}`}
          documents={item.documents}
          onChanged={onApplied}
        />
      ))}
      {activeTask && !gapOwnsTask ? (
        <section className="context-workbench__task-review" data-testid="context-task-review">
          <ContextTaskPane key={activeTask.id} taskId={activeTask.id} onResolved={resolved} />
        </section>
      ) : null}

      {documents.length ? (
        <footer className="context-workbench__section context-workbench__documents">
          <h3>Documents</h3>
          <SourceDocumentList documents={[...documents]} />
        </footer>
      ) : null}
    </article>
  )
}

export interface ContextWorkbenchProps {
  scope: ViewScope
}

export default function ContextWorkbench({ scope }: ContextWorkbenchProps) {
  const navigate = useNavigate()
  const [params, setParams] = useNavigationParams()
  const session = useAuthSession()
  const [refresh, setRefresh] = useState(0)
  const [bookPending, setBookPending] = useState(false)
  // Receipt completion may request refresh before React commits the pending
  // state render. Read the same aggregate synchronously; never skip a real
  // active write or lose a refresh because its completion closure is stale.
  const bookPendingNow = useRef(false)
  const reportBookPending = (pending: boolean) => {
    bookPendingNow.current = pending
    setBookPending(pending)
  }
  const [retainedBook, setRetainedBook] = useState<{
    kind: 'receipt' | 'refresh'
    key: string
    item: ContextCardView
    work: RecordContext['work']
    label: string
  } | null>(null)
  const read = useRead<RecordContext>(recordQuery(scope, { proposed: true }), refresh)
  const work = read.value?.work ?? null
  const view = useMemo(() => contextWorkView(work), [work])
  const queueParam = params.get('queue')
  const selected: ContextQueue = isQueue(queueParam) ? queueParam : 'book'
  const discoverySupported = scope.kind !== 'subchain' && !scope.below_entity_id
  const discoveryIncomplete = work?.book_discovery_complete === false
  const cards = view.cards.filter(
    (item) =>
      item.queues.includes(selected) && (discoverySupported || !isBookDiscoveryCard(item.card))
  )
  const selectedCardId = params.get('card')
  const selectedTaskId = params.get('task')
  const focusedRecovery = params.get('recovery') === '1'
  const recoverySelection = recoveryOwnsSelection(work, focusedRecovery, selectedTaskId)
  const unassignedRemovals = (work?.removal_requests ?? []).filter(
    (request) =>
      !view.cards.some((item) => item.removalRequests.some((row) => row.id === request.id))
  )
  const receiptContext = JSON.stringify([
    session,
    scope,
    selected,
    selectedCardId,
    selectedTaskId,
    params.get('recovery'),
  ])
  useEffect(() => {
    setRetainedBook((previous) => (previous?.key === receiptContext ? previous : null))
  }, [receiptContext])
  const retained =
    retainedBook?.key === receiptContext && (retainedBook.kind === 'receipt' || read.loading)
      ? retainedBook
      : null
  const [bookAnchor, setBookAnchor] = useState<{ key: string; card: BookCardAnchor } | null>(null)
  const directCard =
    cards.find((item) => item.card.id === selectedCardId) ??
    cards.find(
      (item) =>
        tasksForQueue(item, selected).some((task) => task.id === selectedTaskId) ||
        (isBookDiscoveryCard(item.card) &&
          !!selectedTaskId &&
          item.card.source_question_ids?.includes(selectedTaskId))
    ) ??
    (selectedTaskId || selectedCardId || focusedRecovery ? undefined : cards[0])
  const successorId =
    !directCard &&
    !retained &&
    !bookPending &&
    selected === 'book' &&
    bookAnchor?.key === receiptContext
      ? bookContinuation(
          bookAnchor.card,
          cards.map((item) => item.card).filter(isBookDiscoveryCard)
        )
      : null
  const currentCard = directCard ?? cards.find((item) => item.card.id === successorId)
  useEffect(() => {
    if (!currentCard || selected !== 'book' || !isBookDiscoveryCard(currentCard.card)) return
    const anchor = bookCardAnchor(currentCard.card)
    setBookAnchor((previous) =>
      previous?.key === receiptContext && JSON.stringify(previous.card) === JSON.stringify(anchor)
        ? previous
        : { key: receiptContext, card: anchor }
    )
  }, [currentCard, receiptContext, selected])
  useEffect(() => {
    if (!successorId || retained || bookPending) return
    const next = new URLSearchParams(params)
    next.set('card', successorId)
    setParams(next, { replace: true })
  }, [successorId, retained, bookPending, params, setParams])
  // Keep the already admitted Book frame mounted while its completed result is read.
  // Current queue/card counts always come from the refreshed work, never this snapshot.
  const reviewCard = retained?.item ?? currentCard
  const activeCard = retained
    ? (view.cards.find((item) => item.card.id === retained.item.card.id) ??
      (work ? { ...retained.item, tasks: [], removalRequests: [] } : retained.item))
    : currentCard
  const detailWork = work ?? retained?.work ?? null
  const bookReviewRead = useBookReviews(reviewCard, selected, scope)
  const bookReviews = bookReviewRead.reads
  const label =
    read.value && work ? scopeLabel(scope, read.value) : (retained?.label ?? 'Selected context')
  const applied = () => {
    if (selected === 'book' && reviewCard && detailWork)
      setRetainedBook({
        kind: 'receipt',
        key: receiptContext,
        item: reviewCard,
        work: detailWork,
        label,
      })
    setRefresh((n) => n + 1)
  }

  const selectQueue = (queue: ContextQueue) => {
    if (bookPending) return
    setRetainedBook(null)
    const next = new URLSearchParams(params)
    next.set('queue', queue)
    next.delete('card')
    next.delete('task')
    next.delete('recovery')
    setParams(next)
  }

  const selectCard = (id: string) => {
    if (bookPending) return
    setRetainedBook(null)
    const next = new URLSearchParams(params)
    next.set('queue', selected)
    next.set('card', id)
    next.delete('task')
    next.delete('recovery')
    setParams(next)
  }

  return (
    <main className="context-workbench" data-testid="context-workbench">
      <WorkbenchHeader
        scopeLabel={label}
        work={detailWork}
        selected={selected}
        other={read.value?.other_work}
        onSelect={(queue) =>
          queue === 'other' ? navigate(workbenchHref(scope, 'other')) : selectQueue(queue)
        }
      />
      <div className="context-workbench__body">
        {read.loading && !retained ? <Skeleton kind="cards" n={1} height={280} /> : null}
        {read.error ? <ErrorState what="context work" error={read.error} /> : null}
        {detailWork && !recoverySelection && (retained || (!read.loading && !read.error)) ? (
          <>
            <div className="context-workbench__queue-caption">
              <span>
                {read.loading
                  ? 'Refreshing open items…'
                  : read.error
                    ? 'Open-item count unavailable'
                    : selected === 'book' && discoveryIncomplete
                      ? `${cards.length} cards loaded in this queue`
                      : `${cards.length} cards in this queue`}
              </span>
              <span>
                {read.value?.work_scope === 'descendants'
                  ? 'Descendant context'
                  : 'Selected context'}
              </span>
            </div>
            {selected === 'book' && discoveryIncomplete ? (
              <p role="status">Some Book items could not be loaded. The list may be incomplete.</p>
            ) : null}
            {selected === 'book'
              ? unassignedRemovals.map((request) => (
                  <RemovalRequestPane
                    key={request.id}
                    requestId={request.id}
                    targetLabel={request.label ?? undefined}
                    recordHref={recordHref(scope)}
                    onChanged={() => setRefresh((n) => n + 1)}
                  />
                ))
              : null}
            {selectedCardId && !activeCard ? (
              <p role="status">
                {selected === 'book' && discoveryIncomplete
                  ? 'The selected card could not be loaded. Refresh the list before continuing.'
                  : 'The selected card has no open work in this queue. Choose another card to continue.'}
              </p>
            ) : null}
            {!activeCard &&
            (selectedCardId || selectedTaskId) &&
            !(selected === 'book' && discoveryIncomplete) ? (
              <p role="status">
                The selected item is no longer open in this context. Choose an open card.
              </p>
            ) : null}
            {cards.length || activeCard ? (
              <div className="context-workbench__layout">
                <nav className="context-workbench__card-list" aria-label={`${selected} cards`}>
                  {cards.map((item) => (
                    <CardSummary
                      key={item.card.id}
                      item={item}
                      selected={item.card.id === activeCard?.card.id}
                      onSelect={() => selectCard(item.card.id)}
                    />
                  ))}
                </nav>
                {activeCard ? (
                  <CardDetail
                    item={activeCard}
                    work={detailWork}
                    queue={selected}
                    scope={scope}
                    bookReviews={bookReviews}
                    onApplied={applied}
                    onBookPendingChange={reportBookPending}
                    onLocalRefresh={() => {
                      if (!bookPendingNow.current) bookReviewRead.refresh()
                    }}
                    onRefresh={() => {
                      if (bookPendingNow.current) return
                      // Applied receipts keep their frame. Other refreshes bridge
                      // only the pending read, then use the fresh queue selection.
                      setRetainedBook((previous) => {
                        if (
                          previous?.kind === 'receipt' &&
                          previous.key === receiptContext &&
                          currentCard
                        )
                          return previous
                        if (selected !== 'book' || !currentCard || !detailWork) return null
                        return {
                          kind: 'refresh',
                          key: receiptContext,
                          item: currentCard,
                          work: detailWork,
                          label,
                        }
                      })
                      bookReviewRead.refresh()
                      setRefresh((n) => n + 1)
                    }}
                  />
                ) : null}
              </div>
            ) : (
              <p className="context-workbench__empty">
                {selected === 'book' && unassignedRemovals.length
                  ? 'These removal requests await a manager decision.'
                  : selected === 'book' && discoveryIncomplete
                    ? 'No Book cards loaded.'
                    : 'No open cards in this queue for this context.'}
              </p>
            )}
          </>
        ) : null}
        <ContextRecovery
          scope={scope}
          work={work}
          refresh={refresh}
          onChanged={() => setRefresh((n) => n + 1)}
        />
      </div>
    </main>
  )
}
